import type { LucideIcon } from 'lucide-react';
import { Building2, Handshake, LayoutDashboard, Users } from 'lucide-react';
import type { PlatformAbility, PlatformAction, PlatformSubject } from '@/lib/abilities';
import { canOpenNavItem, isNavItemVisible, platformSidebarGroups } from './sidebarNav';

/** Phone (<md) primary tabs. Each is gated by the SAME ability as its sidebar item. */
export interface PhoneTabDef {
    key: string;
    to: string;
    i18nKey: string;
    fallback: string;
    icon: LucideIcon;
    action: PlatformAction;
    subject: PlatformSubject;
    exact?: boolean;
    everyOperator?: boolean;
}

export const PHONE_TABS: PhoneTabDef[] = [
    { key: 'home', to: '/dashboard', i18nKey: 'mobile.tabs.home', fallback: 'Home', icon: LayoutDashboard, action: 'read', subject: 'all', exact: true, everyOperator: true },
    { key: 'merchants', to: '/merchants', i18nKey: 'nav.merchants', fallback: 'Merchants', icon: Building2, action: 'read', subject: 'Merchant' },
    { key: 'crm', to: '/crm', i18nKey: 'mobile.tabs.crm', fallback: 'CRM', icon: Handshake, action: 'read', subject: 'Crm' },
    { key: 'affiliates', to: '/affiliates', i18nKey: 'nav.affiliates', fallback: 'Affiliates', icon: Users, action: 'read', subject: 'Affiliate' },
];

export const isPathActive = (pathname: string, to: string, exact?: boolean): boolean =>
    exact ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);

export interface PhoneMoreRow {
    to: string;
    i18nKey: string;
    icon: LucideIcon;
}

export interface PhoneMoreGroup {
    i18nKey: string;
    label: string;
    rows: PhoneMoreRow[];
}

const TAB_PATHS = new Set(PHONE_TABS.map((tab) => tab.to));

/**
 * Every sidebar destination the user may open, minus the ones already on a tab.
 * Filters exactly like the sidebar's <Can> nesting: a child needs its parent's
 * ability AND its own. Children inherit the parent's icon.
 */
export function buildPhoneMoreGroups(ability: PlatformAbility): PhoneMoreGroup[] {
    return platformSidebarGroups
        .map((group) => {
            const rows: PhoneMoreRow[] = [];
            const seen = new Set<string>();
            const push = (row: PhoneMoreRow): void => {
                if (TAB_PATHS.has(row.to) || seen.has(row.to)) return;
                seen.add(row.to);
                rows.push(row);
            };
            for (const item of group.items) {
                if (!isNavItemVisible(ability, item)) continue;
                if (canOpenNavItem(ability, item)) push({ to: item.to, i18nKey: item.i18nKey, icon: item.icon });
                for (const child of item.children ?? []) {
                    if (ability.can(child.action, child.subject)) push({ to: child.to, i18nKey: child.i18nKey, icon: item.icon });
                }
            }
            return { i18nKey: group.i18nKey, label: group.label, rows };
        })
        .filter((group) => group.rows.length > 0);
}

export function visiblePhoneTabs(ability: PlatformAbility): PhoneTabDef[] {
    return PHONE_TABS.filter((tab) => isNavItemVisible(ability, tab));
}
